import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, FilterX, RefreshCw, Search, Users } from "lucide-react";
import { Editor } from "@tinymce/tinymce-react";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "@/components/Loader";
import Dropdown from "@/components/ui/Dropdown";
import Pagination from "@/components/Common/Pagination";
import MarketingTable from "@/components/marketing/MarketingTable";
import { marketingCampaignsService } from "@/services/marketingCampaignsService";
import { marketingCampaignService } from "@/services/marketingCampaignService";
import { newsletterService } from "@/services/newsletterService";
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useDebounce } from "@/utils/helpers";

const CANDIDATES_TABLE_KEY = "marketing-campaign-form-candidates";
const DEFAULT_PER_PAGE = 50;

const CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

const TINYMCE_INIT = {
  height: 280,
  menubar: false,
  plugins: [
    "advlist",
    "autolink",
    "lists",
    "link",
    "charmap",
    "preview",
    "anchor",
    "searchreplace",
    "visualblocks",
    "code",
    "help",
    "wordcount",
  ],
  toolbar:
    "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright | bullist numlist | link | removeformat | help",
  content_style:
    "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
  skin: "oxide",
  content_css: "default",
  branding: false,
  statusbar: true,
  resize: true,
  promotion: false,
};

const emptyContent = {
  channel: "email",
  subject: "",
  email_body: "",
  sms_body: "",
};

const toFilterOption = (item, fieldName) => {
  if (item == null || item === "") return null;
  if (typeof item !== "string" && typeof item !== "number") return null;

  const rawValue = String(item);
  return {
    label: rawValue,
    value: `${fieldName}:::${rawValue}`,
    field_name: fieldName,
    rawValue,
    category: fieldName,
  };
};

const normalizeFilterTags = (payload) => {
  const tags = Array.isArray(payload?.tags) ? payload.tags : [];

  return tags
    .map((tag) => ({
      name: tag?.name || "",
      fields: (Array.isArray(tag?.fields) ? tag.fields : [])
        .map((field) => {
          const fieldName = field?.field_name || "";
          return {
            field_name: fieldName,
            options: (Array.isArray(field?.values) ? field.values : [])
              .map((value) => toFilterOption(value, fieldName))
              .filter(Boolean),
          };
        })
        .filter((field) => field.field_name && field.options.length > 0),
    }))
    .filter((tag) => tag.name && tag.fields.length > 0);
};

const getTagDropdownOptions = (tag) => {
  if (tag.fields.length === 1) {
    return tag.fields[0].options;
  }

  return tag.fields.flatMap((field) => [
    {
      value: `__group__${field.field_name}`,
      label: field.field_name,
      isGroup: true,
    },
    ...field.options,
  ]);
};

const getSelectedFilterCount = (filterFields = {}) =>
  Object.values(filterFields).reduce(
    (count, values) => count + (Array.isArray(values) ? values.length : 0),
    0,
  );

const filterButtonClassName =
  "h-11 px-3 py-2 bg-primary-dark rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 inline-flex justify-center items-center gap-1 overflow-hidden hover:bg-slate-900 transition-all";

const isEmptyHtml = (value = "") =>
  !value.trim() || value.trim() === "<p><br></p>";

const RequiredMark = () => <span className="text-red-500 ml-0.5">*</span>;

const normalizeTemplateOption = (item) => ({
  value: item.id,
  label: item.name ?? item.title ?? `Template #${item.id}`,
  channel: item.channel || "email",
  subject: item.subject ?? "",
  email_body: item.email_body ?? item.body ?? item.content ?? "",
  sms_body: item.sms_body ?? "",
});

const normalizeCampaign = (item) => {
  const candidates = Array.isArray(item.candidates) ? item.candidates : [];
  const candidateIds = Array.isArray(item.candidate_ids)
    ? item.candidate_ids
    : candidates.map((c) => c.user_id ?? c.id).filter(Boolean);

  return {
    id: item.id,
    name: item.name ?? "",
    template_id: item.template_id ?? item.template?.id ?? null,
    channel:
      item.channel ?? item.template?.channel ?? item.template_channel ?? "email",
    subject: item.subject ?? item.template?.subject ?? "",
    email_body:
      item.email_body ?? item.body ?? item.template?.body ?? item.content ?? "",
    sms_body: item.sms_body ?? item.template?.sms_body ?? "",
    candidate_ids: candidateIds,
  };
};

const CreateMarketingCampaign = () => {
  const navigate = useNavigate();
  const { id: campaignId } = useParams();
  const isEditing = Boolean(campaignId);
  const { getPerPage, setPerPage } = usePaginationStore();
  const { showToast } = useToastStore;

  const [isLoadingCampaign, setIsLoadingCampaign] = useState(isEditing);
  const [campaignName, setCampaignName] = useState("");
  const [content, setContent] = useState(emptyContent);
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCandidates, setShowCandidates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSelectingAllCandidates, setIsSelectingAllCandidates] = useState(false);
  const [filterTags, setFilterTags] = useState([]);
  const [isLoadingFilterTags, setIsLoadingFilterTags] = useState(false);
  const [activeFilterTagNames, setActiveFilterTagNames] = useState([]);
  const [selectedFilterFields, setSelectedFilterFields] = useState({});
  const [appliedFilterFields, setAppliedFilterFields] = useState({});

  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [tempCandidateSearch, setTempCandidateSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedUserIdsByContactId, setSelectedUserIdsByContactId] = useState(
    {},
  );
  const [totalEligibleCandidates, setTotalEligibleCandidates] = useState(0);
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage(CANDIDATES_TABLE_KEY) || DEFAULT_PER_PAGE,
    total: 0,
    last_page: 1,
  });

  const includesEmail =
    content.channel === "email" || content.channel === "both";
  const includesSms = content.channel === "sms" || content.channel === "both";

  const filteredTemplates = useMemo(() => {
    if (content.channel === "email") {
      return templates.filter(
        (template) => template.channel === "email" || template.channel === "both",
      );
    }
    if (content.channel === "sms") {
      return templates.filter(
        (template) => template.channel === "sms" || template.channel === "both",
      );
    }
    return templates.filter((template) => template.channel === "both");
  }, [templates, content.channel]);

  const buildCandidateFilterParams = useCallback(() => {
    const params =
      content.channel === "sms"
        ? { sms_opt_in: 1 }
        : content.channel === "email"
          ? { email_opt_in: 1 }
          : { any_opt_in: 1 };

    const filterTagSelections = Object.entries(appliedFilterFields).reduce(
      (acc, [fieldName, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          acc[fieldName] = values;
        }
        return acc;
      },
      {},
    );

    if (Object.keys(filterTagSelections).length > 0) {
      params.filter_tags = filterTagSelections;
    }

    return params;
  }, [appliedFilterFields, content.channel]);

  const selectedContacts = useMemo(
    () =>
      contacts
        .filter((contact) => selectedUserIds.includes(contact.user_id))
        .map((contact) => contact.id),
    [contacts, selectedUserIds],
  );

  const isAllCandidatesSelected =
    totalEligibleCandidates > 0 &&
    selectedUserIds.length >= totalEligibleCandidates;

  const activeFilterTags = useMemo(
    () =>
      filterTags.filter((tag) => activeFilterTagNames.includes(tag.name)),
    [activeFilterTagNames, filterTags],
  );

  const addFilterOptions = useMemo(
    () =>
      filterTags.map((tag) => ({
        value: tag.name,
        label: tag.name,
      })),
    [filterTags],
  );

  const selectedAddFilterOptions = useMemo(
    () =>
      addFilterOptions.filter((option) =>
        activeFilterTagNames.includes(option.value),
      ),
    [activeFilterTagNames, addFilterOptions],
  );

  const selectAllButtonLabel = isSelectingAllCandidates
    ? "Selecting..."
    : isAllCandidatesSelected
      ? "Unselect All Candidates"
      : "Select All Candidates";

  const goBack = () => {
    localStorage.setItem("marketing_activeTab", "campaigns");
    navigate("/marketing");
  };

  const loadTemplates = useCallback(async () => {
    try {
      setIsLoadingTemplates(true);
      const response = await marketingCampaignService.getCampaigns({
        per_page: 100,
      });
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.templates)
          ? response.templates
          : Array.isArray(response?.campaigns)
            ? response.campaigns
            : Array.isArray(response)
              ? response
              : [];
      return list.map(normalizeTemplateOption);
    } catch (error) {
      showToast({
        message: error.message || "Failed to load templates",
        type: "error",
      });
      return [];
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [showToast]);

  const loadCampaign = useCallback(async () => {
    if (!isEditing) return;

    try {
      setIsLoadingCampaign(true);
      const [response, templateOptions] = await Promise.all([
        marketingCampaignsService.getCampaign(campaignId),
        loadTemplates(),
      ]);

      const campaign = normalizeCampaign(response?.data ?? response);
      setTemplates(templateOptions);
      setCampaignName(campaign.name);
      setContent({
        channel: campaign.channel === "sms" ? "sms" : "email",
        subject: campaign.subject || "",
        email_body: campaign.email_body || "",
        sms_body: campaign.sms_body || "",
      });
      setSelectedUserIds(campaign.candidate_ids || []);
      setShowCandidates(true);

      if (campaign.template_id) {
        const templateOption = templateOptions.find(
          (option) => option.value === campaign.template_id,
        );
        if (templateOption) {
          setSelectedTemplate(templateOption);
        }
      }
    } catch (error) {
      showToast({
        message: error.message || "Failed to load campaign",
        type: "error",
      });
      goBack();
    } finally {
      setIsLoadingCampaign(false);
    }
  }, [campaignId, isEditing, loadTemplates, showToast]);

  useEffect(() => {
    if (isEditing) {
      loadCampaign();
      return;
    }

    const initCreate = async () => {
      const templateOptions = await loadTemplates();
      setTemplates(templateOptions);
    };

    initCreate();
  }, [isEditing, loadCampaign, loadTemplates]);

  const loadFilterTags = useCallback(async () => {
    try {
      setIsLoadingFilterTags(true);
      const response = await newsletterService.getFilterTags();
      setFilterTags(normalizeFilterTags(response));
    } catch (error) {
      showToast({
        message: error.message || "Failed to load filter tags",
        type: "error",
      });
      setFilterTags([]);
    } finally {
      setIsLoadingFilterTags(false);
    }
  }, [showToast]);

  const fetchContacts = useCallback(async () => {
    if (!showCandidates) return;

    try {
      setIsLoadingContacts(true);
      const params = {
        page: serverPagination.current_page,
        per_page: serverPagination.per_page || DEFAULT_PER_PAGE,
      };
      Object.assign(params, buildCandidateFilterParams());
      if (candidateSearch.trim()) {
        params.search = candidateSearch.trim();
      }

      const res = await newsletterService.getSubscribers(params);
      const rows = (res?.data || []).map((item) => ({
        id: item.id,
        user_id: item.user_id ?? item.user?.id,
        name: item.user?.name || "-",
        email: item.user?.email || "-",
        phone_number: item.user?.phone_number || "-",
        gender: item.user?.gender || "-",
        email_opt_in: item.email_opt_in,
        sms_opt_in: item.sms_opt_in,
      }));

      setContacts(rows);
      setSelectedUserIdsByContactId((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (row.user_id) next[row.id] = row.user_id;
        });
        return next;
      });
      if (res?.pagination) {
        setServerPagination((prev) => ({
          ...prev,
          current_page:
            res.pagination.current_page ?? prev.current_page,
          total: res.pagination.total ?? prev.total,
          last_page: res.pagination.last_page ?? prev.last_page,
          // Keep client-selected per_page; API often echoes default (50)
        }));
        if (!candidateSearch.trim() && res.pagination.total != null) {
          setTotalEligibleCandidates(res.pagination.total);
        }
      }
    } catch (error) {
      showToast({
        message: error.message || "Failed to load candidates",
        type: "error",
      });
      setContacts([]);
    } finally {
      setIsLoadingContacts(false);
    }
  }, [
    showCandidates,
    buildCandidateFilterParams,
    candidateSearch,
    serverPagination.current_page,
    serverPagination.per_page,
    showToast,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    if (!showCandidates) return;
    loadFilterTags();
  }, [showCandidates, loadFilterTags]);

  const updateCandidateSearch = (term) => {
    setCandidateSearch(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const debouncedCandidateSearch = useDebounce(updateCandidateSearch, 500);

  const handleCandidateSearchChange = (term) => {
    setTempCandidateSearch(term);
    debouncedCandidateSearch(term);
  };

  const updateContent = (field, value) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const handleTemplateChange = (option) => {
    setSelectedTemplate(option || null);
    if (!isEditing) {
      setShowCandidates(false);
      setSelectedUserIds([]);
    }

    if (option) {
      setContent({
        channel: option.channel || "email",
        subject: option.subject || "",
        email_body: option.email_body || "",
        sms_body: option.sms_body || "",
      });
    }
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
  };

  useEffect(() => {
    if (!selectedTemplate) return;
    const existsInFiltered = filteredTemplates.some(
      (template) => template.value === selectedTemplate.value,
    );
    if (!existsInFiltered) {
      setSelectedTemplate(null);
    }
  }, [filteredTemplates, selectedTemplate]);

  const handleSelectedContactsChange = (updater) => {
    const currentPageContactIds = contacts
      .filter((contact) => selectedUserIds.includes(contact.user_id))
      .map((contact) => contact.id);
    const nextContactIds =
      typeof updater === "function" ? updater(currentPageContactIds) : updater;

    setSelectedUserIds((prev) => {
      const next = new Set(prev);

      contacts.forEach((contact) => {
        const wasSelected = currentPageContactIds.includes(contact.id);
        const isSelected = nextContactIds.includes(contact.id);

        if (wasSelected && !isSelected && contact.user_id) {
          next.delete(contact.user_id);
        }
        if (!wasSelected && isSelected && contact.user_id) {
          next.add(contact.user_id);
        }
      });

      return [...next];
    });
  };

  const validateContent = () => {
    if (!campaignName.trim()) {
      showToast({ message: "Please enter a campaign name", type: "error" });
      return false;
    }

    if (!content.channel) {
      showToast({ message: "Please select a channel", type: "error" });
      return false;
    }

    if (includesEmail && !content.subject.trim()) {
      showToast({ message: "Please enter an email subject", type: "error" });
      return false;
    }

    if (includesEmail && isEmptyHtml(content.email_body)) {
      showToast({ message: "Please enter email content", type: "error" });
      return false;
    }

    if (includesSms && isEmptyHtml(content.sms_body)) {
      showToast({ message: "Please enter an SMS message", type: "error" });
      return false;
    }

    return true;
  };

  const handleShowCandidates = () => {
    if (!validateContent()) return;
    setShowCandidates(true);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleFilterFieldChange = (tag, selected) => {
    const selectedItems = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : [];

    setSelectedFilterFields((prev) => {
      const next = { ...prev };
      tag.fields.forEach((field) => {
        delete next[field.field_name];
      });

      selectedItems.forEach((item) => {
        if (item?.isGroup) return;
        const fieldName = item?.field_name;
        const rawValue = item?.rawValue ?? item?.label;
        if (!fieldName || rawValue == null || rawValue === "") return;
        if (!next[fieldName]) next[fieldName] = [];
        if (!next[fieldName].includes(rawValue)) {
          next[fieldName].push(rawValue);
        }
      });

      return next;
    });
  };

  const handleActiveFilterTagsChange = (selected) => {
    const selectedItems = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : [];
    const nextNames = selectedItems
      .map((item) => item?.value)
      .filter(Boolean);

    const removedTags = filterTags.filter(
      (tag) =>
        activeFilterTagNames.includes(tag.name) &&
        !nextNames.includes(tag.name),
    );

    setActiveFilterTagNames(nextNames);

    if (removedTags.length > 0) {
      setSelectedFilterFields((current) => {
        const next = { ...current };
        removedTags.forEach((tag) => {
          tag.fields.forEach((field) => {
            delete next[field.field_name];
          });
        });
        return next;
      });
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilterFields({ ...selectedFilterFields });
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    setActiveFilterTagNames([]);
    setSelectedFilterFields({});
    setAppliedFilterFields({});
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (page) => {
    setServerPagination((prev) => ({ ...prev, current_page: page }));
  };

  const handlePerPageChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage(CANDIDATES_TABLE_KEY, newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1,
    }));
  };

  const handleUnselectAllCandidates = () => {
    setSelectedUserIds([]);
    showToast({
      message: "All candidates unselected",
      type: "success",
    });
  };

  const handleSelectAllCandidates = async () => {
    if (!validateContent()) return;

    if (isAllCandidatesSelected) {
      handleUnselectAllCandidates();
      return;
    }

    try {
      setIsSelectingAllCandidates(true);

      const perPage = 500;
      let page = 1;
      let lastPage = 1;
      const allUserIds = new Set();
      const filterParams = buildCandidateFilterParams();

      do {
        const response = await newsletterService.getSubscribers({
          ...filterParams,
          page,
          per_page: perPage,
        });

        const rows = Array.isArray(response?.data) ? response.data : [];
        rows.forEach((item) => {
          const userId = item.user_id ?? item.user?.id;
          if (userId) allUserIds.add(userId);
        });

        const responseLastPage = Number(response?.pagination?.last_page || 1);
        lastPage = Number.isFinite(responseLastPage) ? responseLastPage : 1;
        page += 1;
      } while (page <= lastPage);

      setSelectedUserIds([...allUserIds]);
      setTotalEligibleCandidates(allUserIds.size);
      setShowCandidates(true);
      setServerPagination((prev) => ({ ...prev, current_page: 1 }));

      showToast({
        message: `${allUserIds.size} candidate${allUserIds.size === 1 ? "" : "s"} selected`,
        type: "success",
      });
    } catch (error) {
      showToast({
        message: error.message || "Failed to select all candidates",
        type: "error",
      });
    } finally {
      setIsSelectingAllCandidates(false);
    }
  };

  const handleSaveCampaign = async () => {
    if (!validateContent()) return;

    if (!selectedUserIds.length) {
      showToast({
        message: "Please select at least one candidate",
        type: "error",
      });
      return;
    }

    const payload = {
      name: campaignName.trim(),
      channel: content.channel,
      subject: content.subject.trim(),
      email_body: content.email_body.trim(),
      sms_body: content.sms_body.trim(),
      template_id: selectedTemplate?.value || undefined,
      candidate_ids: selectedUserIds,
    };

    try {
      setIsSaving(true);
      if (isEditing) {
        await marketingCampaignsService.updateCampaign(campaignId, payload);
        showToast({ message: "Campaign updated successfully", type: "success" });
      } else {
        await marketingCampaignsService.createCampaign(payload);
        showToast({ message: "Campaign created successfully", type: "success" });
      }
      goBack();
    } catch (error) {
      showToast({
        message:
          error.message ||
          `Failed to ${isEditing ? "update" : "create"} campaign`,
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingCampaign) {
    return (
      <div className="marketing-page-scope flex flex-col min-h-screen bg-background">
        <div className="flex-1 flex justify-center items-center py-20">
          <Loader bgColor="bg-transparent" loadingText="Loading campaign..." />
        </div>
      </div>
    );
  }

  return (
    <div className="marketing-page-scope flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 md:pt-12 pb-10 flex flex-col gap-5 sm:gap-6">
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to campaigns
          </button>
          <div className="flex flex-col gap-1">
            <h1 className="text-foreground text-xl sm:text-2xl font-semibold leading-8">
              {isEditing ? "Edit Campaign" : "Create Campaign"}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base font-normal leading-6">
              Write content yourself or load a template, then select candidates.
            </p>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-sm p-5 sm:p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="campaign-create-name"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Campaign Name
                <RequiredMark />
              </label>
              <input
                id="campaign-create-name"
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                required
                className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2.5 w-full outline-none focus:ring-1 focus:ring-ring"
                placeholder="Please enter campaign name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Channel
                <RequiredMark />
              </label>
              <Dropdown
                className="w-full"
                options={CHANNEL_OPTIONS}
                value={
                  CHANNEL_OPTIONS.find(
                    (option) => option.value === content.channel,
                  ) || CHANNEL_OPTIONS[0]
                }
                placeholder="Select channel"
                onChange={(selectedOption) =>
                  updateContent("channel", selectedOption?.value || "email")
                }
                triggerClassName="!bg-background !border !border-input hover:!border-primary"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-1">
              <label className="block text-sm font-medium text-foreground">
                Template{" "}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              {selectedTemplate && (
                <button
                  type="button"
                  onClick={handleClearTemplate}
                  className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </div>
            <Dropdown
              className="w-full"
              options={filteredTemplates}
              value={selectedTemplate}
              placeholder="Select a template to prefill content"
              searchable
              isLoading={isLoadingTemplates}
              loadingText="Loading templates..."
              onChange={handleTemplateChange}
              triggerClassName="!bg-background !border !border-input hover:!border-primary"
            />
          </div>

          {includesEmail && (
            <>
              <div>
                <label
                  htmlFor="campaign-create-subject"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Email Subject
                  <RequiredMark />
                </label>
                <input
                  id="campaign-create-subject"
                  type="text"
                  value={content.subject}
                  onChange={(e) => updateContent("subject", e.target.value)}
                  required
                  className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2.5 w-full outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Enter email subject..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Email Content
                  <RequiredMark />
                </label>
                <Editor
                  key={`campaign-email-${content.channel}-${selectedTemplate?.value || "manual"}-${isEditing ? campaignId : "new"}`}
                  apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                  value={content.email_body}
                  onEditorChange={(value) =>
                    updateContent("email_body", value)
                  }
                  init={{
                    ...TINYMCE_INIT,
                    placeholder: "Write your email content...",
                  }}
                />
              </div>
            </>
          )}

          {includesSms && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                SMS Message
                <RequiredMark />
              </label>
              <Editor
                key={`campaign-sms-${content.channel}-${selectedTemplate?.value || "manual"}-${isEditing ? campaignId : "new"}`}
                apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                value={content.sms_body}
                onEditorChange={(value) => updateContent("sms_body", value)}
                init={{
                  ...TINYMCE_INIT,
                  height: 200,
                  placeholder: "Write your SMS message...",
                }}
              />
            </div>
          )}
        </div>

        {!showCandidates ? (
          <div className="flex w-full justify-end">
            <button
              type="button"
              onClick={handleShowCandidates}
              className="bg-primary-dark text-white px-4 h-11 rounded-lg shadow-sm font-semibold transition-all hover:bg-slate-900 inline-flex items-center gap-2"
            >
              <Users className="w-5 h-5" />
              Select Candidates
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex w-full flex-wrap items-center gap-3">
              <div className="w-full sm:max-w-[384px] shrink-0">
                <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden w-full">
                  <Search className="w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search candidates"
                    value={tempCandidateSearch}
                    onChange={(e) =>
                      handleCandidateSearchChange(e.target.value)
                    }
                    className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
                  />
                </div>
              </div>

              <div className="ml-auto flex shrink-0 items-center gap-1.5">
                {selectedUserIds.length > 0 && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-600">
                    {selectedUserIds.length} selected
                  </span>
                )}
                <div className="w-[160px]">
                  <Dropdown
                    options={addFilterOptions}
                    selectedValues={selectedAddFilterOptions}
                    onChange={handleActiveFilterTagsChange}
                    placeholder="Add Filter"
                    searchable
                    multiSelect
                    isLoading={isLoadingFilterTags}
                    loadingText="Loading filters..."
                    dropdownWidth="340px"
                    menuMinWidth={340}
                    menuAlign="right"
                    customArrow={<ChevronDown className="w-4 h-4 text-white" />}
                    triggerClassName={`${filterButtonClassName} text-on-dark !w-full !justify-center !bg-primary-dark !text-white !border-transparent hover:!border-transparent hover:!bg-slate-900`}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSelectAllCandidates}
                  disabled={isSelectingAllCandidates}
                  className={`px-6 min-w-[240px] h-11 rounded-lg shadow-sm font-semibold transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50 ${
                    isAllCandidatesSelected
                      ? "bg-card text-foreground border border-input hover:bg-muted"
                      : "bg-primary-dark text-white hover:bg-slate-900"
                  }`}
                >
                  <Users className="w-5 h-5" />
                  {selectAllButtonLabel}
                </button>
              </div>
            </div>

            {(activeFilterTags.length > 0 ||
              getSelectedFilterCount(appliedFilterFields) > 0) && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {activeFilterTags.map((tag) => {
                    const options = getTagDropdownOptions(tag);
                    const selectedValues = tag.fields.flatMap((field) => {
                      const values =
                        selectedFilterFields[field.field_name] || [];
                      return field.options.filter((option) =>
                        values.includes(option.rawValue),
                      );
                    });

                    return (
                      <div key={tag.name} className="w-auto">
                        <Dropdown
                          options={options}
                          selectedValues={selectedValues}
                          onChange={(selected) =>
                            handleFilterFieldChange(tag, selected)
                          }
                          placeholder={tag.name}
                          searchable
                          multiSelect
                          dropdownWidth="420px"
                          menuMinWidth={420}
                          menuAlign="right"
                          customArrow={
                            <ChevronDown className="w-4 h-4 text-white" />
                          }
                          triggerClassName={`${filterButtonClassName} text-on-dark !bg-primary-dark !text-white !border-transparent hover:!border-transparent hover:!bg-primary-dark`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    title="Clear Filters"
                    aria-label="Clear Filters"
                    className="h-11 w-11 shrink-0 rounded-lg bg-primary-dark text-white hover:bg-slate-900 transition-colors inline-flex items-center justify-center"
                  >
                    <FilterX className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyFilters}
                    disabled={isLoadingContacts}
                    className="bg-primary-dark text-white px-4 h-11 rounded-lg shadow-sm font-semibold transition-all hover:bg-slate-900 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {isLoadingContacts ? "Fetching..." : "Apply Filters"}
                  </button>
                </div>
              </div>
            )}

            {isLoadingContacts && contacts.length === 0 ? (
              <div className="flex justify-center items-center py-16">
                <Loader
                  bgColor="bg-transparent"
                  loadingText="Loading candidates..."
                />
              </div>
            ) : (
              <MarketingTable
                contacts={contacts}
                isLoading={isLoadingContacts}
                searchTerm={candidateSearch}
                selectedContacts={selectedContacts}
                setSelectedContacts={handleSelectedContactsChange}
              />
            )}

            {!isLoadingContacts && (
              <Pagination
                currentPage={serverPagination.current_page}
                totalPages={serverPagination.last_page}
                per_page={serverPagination.per_page}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
              />
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={goBack}
                className="px-4 h-11 rounded-lg border border-input text-foreground font-semibold hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCampaign}
                disabled={isSaving}
                className="bg-primary-dark text-white px-5 h-11 rounded-lg shadow-sm font-semibold transition-all hover:bg-slate-900 disabled:opacity-50"
              >
                {isSaving
                  ? isEditing
                    ? "Updating..."
                    : "Creating..."
                  : isEditing
                    ? "Update Campaign"
                    : "Create Campaign"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateMarketingCampaign;
